import React, { useState, useEffect, useCallback, useMemo, ChangeEvent, useRef } from 'react';
import { Arrows } from './Arrows.component';
import { CriterionType, ExamScoringSchema, AssessmentQuality } from '../../../models/assessment.types';
import { ExamTaskGroupSolutionsNavigation } from './../../../pages/Assessment/Assessment.types';
import { Modal } from '../../Modal/Modal.component';
import { useStyles, OrangeSwitch, InputScore } from './ActiveTaskScore.styles';
import { ExaminerFunction, FinalScoreAuthor, VerificationState } from '../../../pages/Assessment/Assessment.types';
import NoGradeRuleFileIcon from '../../../assets/icons/noGradeRuleFile.svg';
import CommentRequired from '../../../assets/icons/commentRequired.svg';
import { SolutionViewMode } from '../../../pages/Solution/Solution.component';
import EnvWindow from '../../../utils/env';
import { AssessmentSessionProcessTypeId, Status } from '../../../services/exams/assessmentSession/assessments.types';
import {
  isExamGradeRuleFileExisting,
  getExamGradeRuleUrl,
} from '../../../services/exams/assessmentSession/assessments.service';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import ScoreDeleteButton from './ScoreDeleteButton.component';
import { Prompt } from '../../Prompt/Prompt.component';
import { SampleAssessmentPill } from '../../SampleAssessmentPill/SampleAssessmentPill.component';
import { getPillTooltip } from '../../../utils/assessmentQuality';
import {
  ToleranceRangeCriterion,
  ToleranceRangeTask,
} from '../../../pages/Sessions/SessionsParametersDialog/ToleranceRange/ToleranceRange.types';
import ProtectedImg from '../../ProtectedImg/ProtectedImg.component';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import GradeChangePrompt from '../GradeChangePrompt.component';
import { useScoring } from '../../../Context/Scoring.context';
import { CriterionTypeIcon } from './CriterionTypeIcon.component';
import {
  areAllLeadingCriterionsAssessed,
  canUserAssessAfterSendingBack,
  getLeadingCriterionsForCriterion,
} from '../../../Context/helpers';
import _ from 'lodash';
import { useLayoutContext } from '../../../Context/Layout.context';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { highContrastMode } from '../../../utils/highContrastChecker';
import TooltipOneComponent from '../../TooltipOne/TooltipOne.component';
interface ActiveTaskScoreProps {
  mode: SolutionViewMode;
  activeTaskIndex: number;
  onTaskChange: (value: 'next' | 'previous') => void;
  onCriterionChange: (value: 'next' | 'previous') => void;
  examTasksLength: number;
  viewType: 'cards' | 'solutions';
  examTaskGroupSolutionsNavigation: ExamTaskGroupSolutionsNavigation;
  examinerFunction: ExaminerFunction;
  examTaskGroupSolutionScoreState: Status;
  examinerId: string;
  handleSelectTask: (taskNumber: string) => void;
  handleSelectCriterion: (taskNumber: string, examScoringSchemaId: string) => void;
  canEditScore: boolean;
  processTypeId: AssessmentSessionProcessTypeId;
  taskToleranceRange: ToleranceRangeTask | null;
  orgzId: string;
  anyAnnotationForGradingProvided: boolean;
  isFullscreen: boolean;
  isAnyModalOpened: boolean;
  keyboardShortcutsDisabled: boolean;
  isSkipLink: boolean;
  isImageLoadError: boolean;
  isVerifier: boolean;
  verificationState?: VerificationState | null;
  areCommentsLoaded: boolean;
}

export const ActiveTaskScore: React.FC<ActiveTaskScoreProps> = (props) => {
  const classes = useStyles();
  const {
    criteriaSetups,
    activeScore,
    activeTask,
    activeCriterion,
    activeCriterionDependentScores,
    maxTaskScore,
    totalTaskScore,
    totalAssessmentScore,
    isCommentForCurrentScoreRequired,
    isCommentForCurrentScoreProvided,
    setNoAnswer,
    updateScoreIfValid,
    activeCriterionIndex,
    deleteTaskScore,
    gradeChangeErrorModalOpenForCriterion,
    setGradeChangeErrorModalOpenForCriterion,
    examTaskGroupSolutionWithTasks,
  } = useScoring();
  const { openAlert } = useLayoutContext();

  if (!activeTask) {
    throw new Error('no activeTask');
  }
  const assessmentSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);
  const annotationGradingMode =
    activeCriterion && activeCriterion.isScoringAnnotationRequired && props.anyAnnotationForGradingProvided;

  const [examGradeRuleOpen, setExamGradeRuleOpen] = useState<boolean>(false);
  const [examGradeRuleEmptyOpen, setExamGradeRuleEmptyOpen] = useState<boolean>(false);
  const [gradeNoAnswerOpen, setGradeNoAnswerOpen] = useState<boolean>(false);
  const [gradeChangeOpen, setGradeChangeOpen] = useState<boolean>(false);
  const [criterionChangeConfirmationModalOpen, setCriterionChangeConfirmationModalOpen] = useState<boolean>(false);
  const [criterionChangeContinueCallback, setCriterionChangeContinueCallback] = useState<(() => void) | null>(null);
  const [onGradeChangeConfirm, setOnGradeChangeConfirm] = useState<(() => void) | null>(null);
  const [onGradeChangeCancel, setOnGradeChangeCancel] = useState<(() => void) | null>(null);
  const [, setPage] = useState<string>('');
  const [isDownloaded, setIsDownloaded] = useState<boolean>(false);
  const [isValidInput, setIsValidInput] = useState<boolean>(true);
  const [inputScore, setInputScore] = useState<string | undefined>(undefined);
  const [examScoringSchemas, setExamScoringSchemas] = useState<ExamScoringSchema[] | null>(null);
  const [isHeaderFocused, setIsHeaderFocused] = useState<boolean>(false);

  const inputScoreRef = useRef<HTMLInputElement>(null);

  const border = highContrastMode ? 'solid 1px white' : 'none';

  const isPZEReviewVerifier = props.examinerFunction === ExaminerFunction.PZE;
  const isNoChanges = props.verificationState === VerificationState.noChanges;
  const isScoreSelectedPreviewBySupervisor =
    props.verificationState === VerificationState.scoreSelected &&
    [ExaminerFunction.PZE, ExaminerFunction.KGZ, ExaminerFunction.KK].includes(props.examinerFunction);
  const showVerifierScore =
    isPZEReviewVerifier &&
    (props.verificationState === VerificationState.pendingApproval ||
      props.verificationState === VerificationState.scoreSelected ||
      isNoChanges);
  const effectiveNoAnswer =
    props.verificationState === VerificationState.scoreSelected &&
    examTaskGroupSolutionWithTasks?.finalScoreAuthor === FinalScoreAuthor.Verifier
      ? activeTask.verifierNoAnswer ?? activeTask.noAnswer
      : activeTask.noAnswer;
  const verifierTotal = isPZEReviewVerifier
    ? isNoChanges
      ? activeTask.examScoringSchemas.reduce((sum, s) => sum + (s.score ?? 0), 0)
      : activeTask.examScoringSchemas.some((s) => s.verifierScore !== null)
      ? activeTask.examScoringSchemas.reduce((sum, s) => sum + (s.verifierScore ?? 0), 0)
      : null
    : null;

  const getVerifierCriterionQuality = (
    examinerScore: number | null,
    verifierScore: number | null,
  ): AssessmentQuality | null => {
    if (isNoChanges) return AssessmentQuality.Accurate;
    if (examinerScore === null || verifierScore === null) return null;
    return examinerScore === verifierScore ? AssessmentQuality.Accurate : AssessmentQuality.Inaccurate;
  };

  useEffect(() => {
    if (
      activeCriterion &&
      activeTask &&
      !areAllLeadingCriterionsAssessed(criteriaSetups, activeCriterion, activeTask)
    ) {
      setGradeChangeErrorModalOpenForCriterion(activeCriterion);
    }
    setIsValidInput(true);
  }, [activeCriterion, activeTask, criteriaSetups]);

  useEffect(() => {
    if (activeTask && activeCriterion) {
      const schemasLength = activeTask.examScoringSchemas.findIndex(
        (e) => e.examScoringSchemaId === activeCriterion.examScoringSchemaId,
      );
      const schemas: any = [];
      activeTask.examScoringSchemas.slice(schemasLength).map((scoringSchema: ExamScoringSchema) => {
        schemas.push(scoringSchema);
        if (scoringSchema.examScoringSchemaId === activeCriterion.examScoringSchemaId) {
          return;
        }
      });
      return setExamScoringSchemas(schemas);
    } else setExamScoringSchemas(activeTask.examScoringSchemas);
  }, [activeTask, activeCriterion]);

  const getSheetLabel = (): string => {
    return assessmentSession ? assessmentSession.assessmentSessionName : '';
  };

  const needToDisplayRequiredCommentModal = () => {
    return (
      props.canEditScore &&
      isCommentForCurrentScoreRequired &&
      !isCommentForCurrentScoreProvided &&
      !effectiveNoAnswer &&
      !isScoreSelectedPreviewBySupervisor
    );
  };

  const focusInputScore = () => {
    if (inputScoreRef && inputScoreRef.current) inputScoreRef.current.focus();
  };

  useEffect(() => {
    focusInputScore();
  }, [inputScoreRef.current]);

  const handleArrowsNavigation = useCallback(
    (value: 'up' | 'down' | 'previous' | 'next') => {
      const continueCallback = () => {
        setInputScore(undefined);
        setIsValidInput(true);
        switch (value) {
          case 'down':
            props.onCriterionChange('next');
            break;
          case 'up':
            props.onCriterionChange('previous');
            break;
          case 'next':
            props.onTaskChange('next');
            break;
          case 'previous':
            props.onTaskChange('previous');
            break;
        }
      };

      if (needToDisplayRequiredCommentModal()) {
        setCriterionChangeConfirmationModalOpen(true);
        setCriterionChangeContinueCallback(() => () => {
          continueCallback();
        });
      } else {
        continueCallback();
      }
    },
    [
      isCommentForCurrentScoreRequired,
      isCommentForCurrentScoreProvided,
      props.onCriterionChange,
      props.onTaskChange,
      inputScoreRef.current,
    ],
  );

  const handleExamGradeRuleOpen = () => {
    if (activeTask.gradeRule) {
      isExamGradeRuleFileExisting(activeTask.gradeRule.id).then(
        () => {
          setExamGradeRuleOpen(true);
        },
        () => {
          setExamGradeRuleEmptyOpen(true);
        },
      );
    } else {
      setExamGradeRuleEmptyOpen(true);
    }
  };

  const handleExamGradeRuleClose = () => {
    setExamGradeRuleOpen(false);
  };

  const handleExamGradeRuleEmptyClose = () => {
    setExamGradeRuleEmptyOpen(false);
  };

  const handleGradeChangeClose = () => {
    setGradeChangeOpen(false);
    if (onGradeChangeCancel) {
      onGradeChangeCancel();
    }
  };

  useEffect(() => {
    if (!isDownloaded) {
      setPage(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamGradeRule/${activeTask.gradeRule.id}/document`);
      setIsDownloaded(true);
    }
  });

  const onValueChange = (value: string | null) => {
    if (value === 'k') return; // blokada przy aktywacji z klawiatury wstawienia komentarza na stronie
    setInputScore(value !== null ? value : '');
    let isValueValid = false;
    let numberValue: number | null;
    const isValueContainsOnlyDigits = /^[0-9]+$/.test(value ? value : '');
    if (value !== null && value !== '' && isValueContainsOnlyDigits && activeCriterion) {
      numberValue = parseFloat(value);
      isValueValid = !!activeCriterionDependentScores.some((e) => e.score === numberValue);
    } else {
      numberValue = null;
      isValueValid = value !== null && value !== '' ? isValueContainsOnlyDigits : true;
    }
    setIsValidInput(isValueValid);
    const promise = updateScoreIfValid(isValueValid, numberValue);
    if (promise) {
      promise.then(
        () => setInputScore(undefined),
        () => openAlert('error'),
      );
    }
    if (isValueValid) {
      setInputScore(value !== null ? value : undefined);
    }
  };

  const handleScoreDelete = () => {
    setGradeChangeOpen(true);
    setOnGradeChangeConfirm(() => () => {
      deleteTaskScore();
      setGradeChangeOpen(false);
    });
    setOnGradeChangeCancel(() => () => {
      setOnGradeChangeCancel(null);
    });
  };

  const handleGradeNoAnswerClose = () => {
    setGradeNoAnswerOpen(false);
    if (onGradeChangeCancel) {
      onGradeChangeCancel();
    }
  };

  const handleGradeChangeConfirm = () => {
    if (onGradeChangeConfirm) {
      onGradeChangeConfirm();
    }
  };

  const handleNoAnswerChange = useCallback(() => {
    if (activeTask.noAnswer || totalTaskScore === null) {
      setNoAnswer(!activeTask.noAnswer);
    } else {
      setGradeNoAnswerOpen(true);
    }
  }, [activeTask, totalTaskScore]);

  const handleNoAnswerModalConfirm = useCallback(() => {
    setGradeNoAnswerOpen(false);
    setNoAnswer(true);
  }, [activeCriterion, activeTask]);

  const isScoreDeleteButtonDisabled = (): boolean => {
    return (
      totalTaskScore === null ||
      (props.examTaskGroupSolutionScoreState !== Status.Open &&
        !canUserAssessAfterSendingBack(
          props.examinerFunction,
          props.examTaskGroupSolutionScoreState,
          props.taskToleranceRange !== null,
          props.isVerifier,
        )) ||
      props.examTaskGroupSolutionScoreState === Status.Assessed
    );
  };

  const handleSelectCriterium = useCallback(
    (scoringSchema: ExamScoringSchema) => {
      const continueCallback = () => {
        setInputScore(undefined);
        props.handleSelectCriterion(activeTask.taskNumber, scoringSchema.examScoringSchemaId);
      };

      if (needToDisplayRequiredCommentModal()) {
        setCriterionChangeConfirmationModalOpen(true);
        setCriterionChangeContinueCallback(() => () => {
          continueCallback();
        });
      } else {
        continueCallback();
      }
    },
    [
      isCommentForCurrentScoreRequired,
      isCommentForCurrentScoreProvided,
      props.handleSelectCriterion,
      activeTask.taskNumber,
    ],
  );

  useEffect(() => {
    setInputScore(undefined);
  }, [activeScore]);

  const handleTaskClick = useCallback(() => {
    const continueCallback = () => {
      props.handleSelectTask(activeTask.taskNumber);
    };

    if (needToDisplayRequiredCommentModal()) {
      setCriterionChangeConfirmationModalOpen(true);
      setCriterionChangeContinueCallback(() => () => {
        continueCallback();
      });
    } else {
      continueCallback();
    }
  }, [isCommentForCurrentScoreRequired, isCommentForCurrentScoreProvided, props.handleSelectTask]);

  const handleContinueCriterionChange = () => {
    criterionChangeContinueCallback && criterionChangeContinueCallback();
    setCriterionChangeContinueCallback(null);
    setCriterionChangeConfirmationModalOpen(false);
  };

  const handleConfirmCommentAdding = () => {
    setCriterionChangeConfirmationModalOpen(false);
    setCriterionChangeContinueCallback(null);
  };

  const leadingCriterionsIds = useMemo((): string[] => {
    return criteriaSetups
      ? _.uniq(criteriaSetups.filter((e) => e.leadingSchemaId).map((item) => item.leadingSchemaId))
      : [];
  }, [criteriaSetups]);

  const dependantCriterionsIds = useMemo((): string[] => {
    return criteriaSetups
      ? _.uniq(criteriaSetups.filter((e) => e.leadingSchemaId).map((item) => item.dependentSchemaId))
      : [];
  }, [criteriaSetups]);

  const isLeadingCriterion = (criterion: ExamScoringSchema) => {
    return leadingCriterionsIds.some((e) => e === criterion.examScoringSchemaId);
  };

  const isDependentCriterion = (criterion: ExamScoringSchema) => {
    return dependantCriterionsIds.some((e) => e === criterion.examScoringSchemaId);
  };

  const getLeadingCriterions = (criterion: ExamScoringSchema): ExamScoringSchema[] => {
    return getLeadingCriterionsForCriterion(criterion, criteriaSetups, activeTask);
  };

  const getActiveTaskBackground = (): string => {
    return props.isImageLoadError ? '#494949' : '#3C419A';
  };

  return (
    <>
      {!activeCriterion && (
        <>
          <div className={classes.container} style={{ border: border, backgroundColor: getActiveTaskBackground() }}>
            <Arrows
              onChange={handleArrowsNavigation}
              isFirstTask={props.activeTaskIndex === 0}
              isLastTask={props.activeTaskIndex === props.examTasksLength - 1}
              isFirstCriterion={props.activeTaskIndex === 0 && (!activeCriterion || activeCriterionIndex === 0)}
              isLastCriterion={
                props.activeTaskIndex === props.examTasksLength - 1 &&
                activeCriterionIndex === activeTask.examScoringSchemas.length - 1
              }
              viewType={props.viewType}
              isFullscreen={props.isFullscreen}
              isAnyModalOpened={props.isAnyModalOpened || examGradeRuleOpen || examGradeRuleEmptyOpen}
              hasNextRgz={props.examTaskGroupSolutionsNavigation.nextId !== null}
              hasPreviousRgz={props.examTaskGroupSolutionsNavigation.prevId !== null}
              keyboardShortcutsDisabled={props.keyboardShortcutsDisabled || isHeaderFocused}
              isSkipLink={props.isSkipLink}
              backgroundColor={getActiveTaskBackground()}
            />
            <div
              className={classes.activeTaskScoreContainer}
              style={{ border: border, backgroundColor: getActiveTaskBackground() }}
            >
              <div className={classes.activeTaskScore}>
                <div className={classes.header}>
                  <TooltipOne title="Wyświetl zasady oceniania" placement="left">
                    <h2
                      className={[classes.headerName, classes.activeHeader].join(' ')}
                      onClick={handleExamGradeRuleOpen}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleExamGradeRuleOpen();
                        }
                      }}
                      onFocus={() => setIsHeaderFocused(true)}
                      onBlur={() => setIsHeaderFocused(false)}
                      tabIndex={0}
                    >
                      Zadanie {activeTask.taskNumber}
                    </h2>
                  </TooltipOne>
                  {totalTaskScore !== null && (
                    <div className={classes.sampleAssessmentScoreContainer}>
                      {props.examTaskGroupSolutionScoreState === Status.Closed && totalAssessmentScore !== null && (
                        <SampleAssessmentPill
                          sampleAssessmentPoints={totalAssessmentScore}
                          tooltip={getPillTooltip(
                            props.processTypeId,
                            props.taskToleranceRange?.toleranceRange?.toleranceRangeType,
                            props.taskToleranceRange?.toleranceRange?.toleranceRangeValue,
                          )}
                          assessmentQuality={activeTask.assessmentQuality}
                          processTypeId={props.processTypeId}
                        />
                      )}
                      {showVerifierScore && verifierTotal !== null && (
                        <SampleAssessmentPill
                          sampleAssessmentPoints={verifierTotal}
                          assessmentQuality={null}
                          color="#333333"
                          processTypeId={props.processTypeId}
                        />
                      )}
                      <div className={[classes.headerScore, classes.activeHeader].join(' ')}>
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {props.mode === SolutionViewMode.sampleAssessmentGroupAssignment
                            ? '-'
                            : totalTaskScore !== null
                            ? totalTaskScore
                            : '-'}
                          /{maxTaskScore}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.switch}>
                Brak odpowiedzi
                <TooltipOneComponent
                  title={
                    !(props.canEditScore && activeTask.gradeRule.noAnswer)
                      ? props.canEditScore
                        ? 'Opcja „Brak odpowiedzi” jest wyłączona dla tego zadania'
                        : "Nie możesz zmienić opcji 'Brak odpowiedzi', ponieważ nie masz dostępu do edycji pracy."
                      : ''
                  }
                  placement="top"
                >
                  <span>
                    <OrangeSwitch
                      disabled={!(props.canEditScore && activeTask.gradeRule.noAnswer)}
                      checked={!!effectiveNoAnswer}
                      onChange={handleNoAnswerChange}
                      size="small"
                    />
                  </span>
                </TooltipOneComponent>
              </div>
              {props.canEditScore &&
                !effectiveNoAnswer &&
                props.mode !== SolutionViewMode.sampleAssessmentGroupAssignment && (
                  <div className={classes.cleanButtonContainer}>
                    <ScoreDeleteButton isDisabled={isScoreDeleteButtonDisabled()} onClick={handleScoreDelete} />
                  </div>
                )}
            </div>
          </div>
        </>
      )}

      {examScoringSchemas &&
        examScoringSchemas.map((scoringSchema: ExamScoringSchema, index) => {
          const criterionToleranceRange = props.taskToleranceRange?.criterion.find(
            (criterionData: ToleranceRangeCriterion) => criterionData.criterionId === scoringSchema.examScoringSchemaId,
          );

          const showQualityPill =
            props.examTaskGroupSolutionScoreState === Status.Closed && scoringSchema.sampleScore !== null;

          const isLeading = isLeadingCriterion(scoringSchema);
          const isDependent = isDependentCriterion(scoringSchema);

          return (
            <React.Fragment key={activeTask.taskId + index + scoringSchema.examScoringSchemaId}>
              {activeCriterion && activeCriterion.examScoringSchemaId === scoringSchema.examScoringSchemaId ? (
                <div
                  className={classes.container}
                  style={{ border: border, backgroundColor: getActiveTaskBackground() }}
                >
                  <Arrows
                    onChange={handleArrowsNavigation}
                    isFirstTask={props.activeTaskIndex === 0}
                    isFirstCriterion={props.activeTaskIndex === 0 && activeCriterionIndex === 0}
                    isLastTask={props.activeTaskIndex === props.examTasksLength - 1}
                    isLastCriterion={
                      props.activeTaskIndex === props.examTasksLength - 1 &&
                      activeCriterionIndex === activeTask.examScoringSchemas.length - 1
                    }
                    isFullscreen={props.isFullscreen}
                    isAnyModalOpened={props.isAnyModalOpened || examGradeRuleOpen || examGradeRuleEmptyOpen}
                    keyboardShortcutsDisabled={props.keyboardShortcutsDisabled || isHeaderFocused}
                    isSkipLink={props.isSkipLink}
                  />
                  <div className={classes.activeTaskScorePanel}>
                    <div
                      className={classes.activeTaskScoreContainer}
                      style={{ border: border, backgroundColor: getActiveTaskBackground() }}
                    >
                      <div className={classes.activeTaskScore}>
                        <div className={classes.score}>
                          <ul className={classes.activeScoreList}>
                            <li style={{ marginLeft: isLeading || isDependent ? '26px' : undefined }}>
                              <div
                                style={{ position: 'absolute', left: '0px', display: 'flex', flexDirection: 'column' }}
                              >
                                {isLeading && (
                                  <TooltipOne title="Kryterium wiodące" placement="top">
                                    <div>
                                      <CriterionTypeIcon criterionType={CriterionType.leadingCriterion} />
                                    </div>
                                  </TooltipOne>
                                )}
                                {isDependent && ( // to do
                                  <TooltipOne
                                    title={`Kryterium zależne od kryterium wiodącego ${getLeadingCriterions(
                                      scoringSchema,
                                    )
                                      .map((item) => item.criterionName)
                                      .join(', ')}`}
                                    placement="top"
                                  >
                                    <div>
                                      <CriterionTypeIcon criterionType={CriterionType.dependentCriterion} />
                                    </div>
                                  </TooltipOne>
                                )}
                              </div>
                              <TooltipOne title={scoringSchema.criterionName} placement="left">
                                <div>
                                  {scoringSchema.label} {scoringSchema.criterionName}
                                </div>
                              </TooltipOne>
                            </li>
                          </ul>
                          {showQualityPill && scoringSchema.sampleScore !== null && (
                            <div style={{ position: 'absolute', right: '60px', marginTop: '9px' }}>
                              <SampleAssessmentPill
                                sampleAssessmentPoints={scoringSchema.sampleScore}
                                tooltip={getPillTooltip(
                                  props.processTypeId,
                                  criterionToleranceRange?.toleranceRange?.toleranceRangeType,
                                  criterionToleranceRange?.toleranceRange?.toleranceRangeValue,
                                )}
                                assessmentQuality={scoringSchema.assessmentQuality}
                                processTypeId={props.processTypeId}
                              />
                            </div>
                          )}
                          {showVerifierScore && (scoringSchema.verifierScore !== null || isNoChanges) && (
                            <div style={{ position: 'absolute', right: '43px', marginTop: '9px' }}>
                              <SampleAssessmentPill
                                sampleAssessmentPoints={isNoChanges ? scoringSchema.score : scoringSchema.verifierScore}
                                assessmentQuality={getVerifierCriterionQuality(
                                  scoringSchema.score,
                                  scoringSchema.verifierScore,
                                )}
                                processTypeId={props.processTypeId}
                              />
                            </div>
                          )}
                        </div>
                        <div className={classes.score}>
                          <ul className={classes.activeInput}>
                            {!activeCriterion?.isScoringAnnotationRequired &&
                            props.canEditScore &&
                            !effectiveNoAnswer &&
                            !annotationGradingMode ? (
                              <li className={classes.inputScore}>
                                <InputScore
                                  inputRef={inputScoreRef}
                                  id="input-score"
                                  variant="standard"
                                  autoComplete="off"
                                  onChange={
                                    (e: ChangeEvent<HTMLInputElement>) => onValueChange(e.target.value) //, scoringSchema)
                                  }
                                  label="Wartość"
                                  value={
                                    inputScore !== undefined ? inputScore : activeScore !== null ? activeScore : ''
                                  }
                                  focused
                                  inputProps={{
                                    style: {
                                      color: '#FFFFFF',
                                      width: '24px',
                                      padding: '5px 5px 0px 0px',
                                      fontSize: '14px',
                                    },
                                  }}
                                  InputLabelProps={{
                                    style: {
                                      color: !isValidInput ? '#FF5E5E' : '#FFFFFF',
                                    },
                                  }}
                                />
                                /{Math.max(...scoringSchema.examScoreValues.map((e) => e.score))}
                              </li>
                            ) : (
                              <li key={scoringSchema.score} style={{ paddingRight: '12px' }}>
                                <span style={{ whiteSpace: 'nowrap' }}>
                                  {props.examinerFunction === undefined ? (
                                    <b>
                                      {props.mode === SolutionViewMode.sampleAssessmentGroupAssignment
                                        ? scoringSchema.sampleScore
                                        : scoringSchema.score}
                                    </b>
                                  ) : (
                                    <>
                                      {props.mode === SolutionViewMode.sampleAssessmentGroupAssignment
                                        ? scoringSchema.sampleScore
                                        : scoringSchema.score}
                                    </>
                                  )}
                                  /
                                  {props.mode === SolutionViewMode.sampleAssessmentGroupAssignment
                                    ? scoringSchema.examScoreValues[0]
                                    : Math.max(...scoringSchema.examScoreValues.map((e) => e.score))}
                                </span>
                              </li>
                            )}
                            {!isValidInput && (
                              <div className={classes.errorMessage}>Wprowadzona wartość jest nieprawidłowa</div>
                            )}
                            {isCommentForCurrentScoreRequired &&
                              !isCommentForCurrentScoreProvided &&
                              !effectiveNoAnswer &&
                              !isScoreSelectedPreviewBySupervisor &&
                              props.areCommentsLoaded && (
                                <TooltipOne
                                  title="Napisz komentarz do tego kryterium w górnym pasku z komentarzami"
                                  placement="left"
                                >
                                  <img
                                    src={CommentRequired}
                                    alt="Ikona Wymagany komentarz"
                                    className={classes.commentRequiredIcon}
                                    style={{
                                      top: annotationGradingMode ? '38px' : '65px',
                                    }}
                                  />
                                </TooltipOne>
                              )}
                          </ul>
                        </div>
                      </div>
                    </div>
                    {annotationGradingMode && (
                      <TooltipOne
                        title="To kryterium należy oceniać używając adnotacji do oceny zdefiniowanych w pasku narzędzi z adnotacjami"
                        placement="left"
                      >
                        <div className={classes.annotationScoringInfo}>
                          <>
                            <InfoOutlinedIcon
                              htmlColor="white"
                              style={{
                                verticalAlign: 'middle',
                                margin: '3px 8px',
                              }}
                            />
                            Ocenianie adnotacjami
                          </>
                        </div>
                      </TooltipOne>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => handleSelectCriterium(scoringSchema)}
                  className={[classes.taskScore, showQualityPill ? classes.rowWithQualityPill : undefined].join(' ')}
                  style={{ border }}
                >
                  <div className={classes.criterionScore}>
                    <ul className={classes.scoreList}>
                      <li>
                        <div style={{ position: 'absolute', left: '0px' }}>
                          {isLeading && (
                            <TooltipOne title="Kryterium wiodące" placement="top">
                              <div>
                                <CriterionTypeIcon criterionType={CriterionType.leadingCriterion} />
                              </div>
                            </TooltipOne>
                          )}
                        </div>
                        <TooltipOne title={scoringSchema.criterionName} placement="left">
                          <div>
                            {scoringSchema.label} {scoringSchema.criterionName}
                          </div>
                        </TooltipOne>
                      </li>
                    </ul>
                  </div>
                  <div className={classes.sampleAssessmentScoreContainer}>
                    {showQualityPill && scoringSchema.sampleScore !== null && (
                      <SampleAssessmentPill
                        sampleAssessmentPoints={scoringSchema.sampleScore}
                        assessmentQuality={scoringSchema.assessmentQuality}
                        processTypeId={props.processTypeId}
                        tooltip={
                          (showQualityPill
                            ? getPillTooltip(
                                props.processTypeId,
                                criterionToleranceRange?.toleranceRange?.toleranceRangeType,
                                criterionToleranceRange?.toleranceRange?.toleranceRangeValue,
                              )
                            : '') as string
                        }
                      />
                    )}
                    {showVerifierScore && (scoringSchema.verifierScore !== null || isNoChanges) && (
                      <SampleAssessmentPill
                        sampleAssessmentPoints={isNoChanges ? scoringSchema.score : scoringSchema.verifierScore}
                        assessmentQuality={getVerifierCriterionQuality(
                          scoringSchema.score,
                          scoringSchema.verifierScore,
                        )}
                        processTypeId={props.processTypeId}
                      />
                    )}
                    <div className={classes.score}>
                      <ul className={classes.scoreList}>
                        <li style={{ marginLeft: '10px' }}>
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {props.mode == SolutionViewMode.sampleAssessmentGroupAssignment
                              ? ''
                              : scoringSchema.score === null
                              ? ''
                              : scoringSchema.score}
                            /
                            {props.mode == SolutionViewMode.sampleAssessmentGroupAssignment
                              ? scoringSchema.examScoreValues[0]
                              : Math.max(...scoringSchema.examScoreValues.map((e) => e.score))}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      {activeTask.gradeRule && (
        <Modal
          open={examGradeRuleOpen}
          size="xl"
          title={`Zasady oceniania dla arkusza: ${getSheetLabel()}, zadanie ${activeTask.taskNumber}`}
          xCloseButton={handleExamGradeRuleClose}
          draggable
        >
          <ProtectedImg
            src={getExamGradeRuleUrl(activeTask.gradeRule.id)}
            style={{ width: '100%' }}
            alt="zasady oceniania dla arkusza"
          />
        </Modal>
      )}

      <Modal
        open={examGradeRuleEmptyOpen}
        size="xl"
        title={`Zasady oceniania dla arkusza: ${getSheetLabel()}, zadanie ${activeTask.taskNumber}`}
        xCloseButton={handleExamGradeRuleEmptyClose}
        draggable
      >
        <div className={classes.grayContainer}>
          <img src={NoGradeRuleFileIcon} alt="ikona brak zasad oceniania dla arkusza" />
          <div className={classes.text}>
            W tej chwili w systemie nie ma zasad oceniania
            <br />
            dla arkusza: {getSheetLabel()}, zadanie {activeTask.taskNumber}
          </div>
        </div>
      </Modal>

      <GradeChangePrompt
        open={gradeChangeOpen}
        onCancel={handleGradeChangeClose}
        onConfirm={handleGradeChangeConfirm}
        activeTask={activeTask}
        activeCriterion={activeCriterion}
      />

      <Prompt
        type="confirmation"
        open={gradeNoAnswerOpen}
        size="xs"
        onCancel={handleGradeNoAnswerClose}
        onConfirm={handleNoAnswerModalConfirm}
        mainActionButtonText="TAK"
        mainText={
          <>
            Zaznaczenie BO spowoduje usunięcie punktacji <br /> we wszystkich kryteriach tego zadania.
          </>
        }
        subTexts={[
          {
            text: 'Czy chcesz kontynuować?',
          },
        ]}
      />
      <Prompt
        type="confirmation"
        size="sm"
        open={criterionChangeConfirmationModalOpen}
        onConfirm={handleConfirmCommentAdding}
        onCancel={handleContinueCriterionChange}
        mainActionButtonText="TAK"
        cancelButtonText="NIE"
        isBold={true}
        mainText={
          <>
            Liczba przyznanych punktów do kryterium
            <div style={{ fontSize: 'smaller' }}>{activeCriterion?.criterionName}</div> wymaga komentarza.
          </>
        }
        subTexts={[
          {
            text: (
              <>
                Wymuszony komentarz należy wpisać korzystając z głównego paska komentarzy w górnej części ekranu
                oceniania. <br />
                Czy chcesz go wpisać teraz?
              </>
            ),
          },
        ]}
      />
      {gradeChangeErrorModalOpenForCriterion && (
        <Prompt
          type="warning"
          size="sm"
          open={true}
          onConfirm={() => {
            const allLeadingCriterions = getLeadingCriterions(gradeChangeErrorModalOpenForCriterion);
            const firstNotAssessedLeadingCriterion = allLeadingCriterions.find((item) => item.score === null);
            setGradeChangeErrorModalOpenForCriterion(null);
            if (firstNotAssessedLeadingCriterion) {
              handleSelectCriterium(firstNotAssessedLeadingCriterion);
            } else {
              props.handleSelectTask(activeTask.taskNumber);
            }
          }}
          mainActionButtonText="OK"
          isBold={true}
          mainText={<>Nie można wprowadzić punktacji</>}
          subTexts={[
            {
              text: (
                <>
                  Należy najpierw ocenić kryterium wiodące <br />
                  {getLeadingCriterions(gradeChangeErrorModalOpenForCriterion)
                    .map((item) => item.criterionName)
                    .join(', ')}
                  <br />
                  by móc wprowadzić punktację dla kryterium zależnego.
                </>
              ),
            },
          ]}
        />
      )}
    </>
  );
};
